import { useCallback, useEffect, useState } from 'react';

import { useGetLoggedInUser } from '@revelio/auth';
import { StorageKind, StorageSubscribe } from '@revelio/core';
import { TaxonomyType } from '@revelio/data-access';

import { useCurrentReferenceTaxonomyVersion } from '../../../../shared/selection-lists/hooks/useReferenceTaxonomyVersions';
import { useCurrentRoleTaxonomyVersion } from '../../../../shared/selection-lists/hooks/useRoleTaxonomyAdapter';
import {
  FilterId,
  type PrimaryFilterId,
  type TreeSelection,
} from '../types/filters';
import {
  addRecentEntities,
  getRecentEntities,
  getRecentEntitiesStorageKey,
  removeRecentEntities,
} from '../utils/recentEntities';

export const useRecentEntities = (filterId?: PrimaryFilterId) => {
  const { version: roleTaxonomyVersion } = useCurrentRoleTaxonomyVersion();
  const { version: skillTaxonomyVersion } = useCurrentReferenceTaxonomyVersion({
    taxonomyType: TaxonomyType.Skill,
  });
  const { version: activityTaxonomyVersion } =
    useCurrentReferenceTaxonomyVersion({
      taxonomyType: TaxonomyType.Activity,
      pause: filterId !== FilterId.ACTIVITY,
    });
  // Role, skill and activity ids are taxonomy-relative, so their recents are
  // keyed by the live taxonomy version; the other primaries have no version.
  const version = (() => {
    if (filterId === FilterId.ROLE) return roleTaxonomyVersion;
    if (filterId === FilterId.CUSTOM_SKILL) return skillTaxonomyVersion;
    if (filterId === FilterId.ACTIVITY) return activityTaxonomyVersion;
    return undefined;
  })();
  const { loggedInUser } = useGetLoggedInUser();
  const userId = loggedInUser?.id ?? undefined;

  // Recents are written by OTHER instances of this hook (a primary-filter tag
  // applying a selection, another popover removing a row), so consumers only
  // stay current if they subscribe to the key themselves.
  const storageKey =
    filterId && userId ? getRecentEntitiesStorageKey(userId, filterId) : null;
  const [storageRevision, setStorageRevision] = useState(0);

  useEffect(() => {
    if (!storageKey) return;
    return StorageSubscribe.subscribe(StorageKind.Local, storageKey, () =>
      setStorageRevision((revision) => revision + 1)
    );
  }, [storageKey]);

  // The read is always live; `storageRevision` is here only to refresh this
  // callback's identity, which is what consumers key their recents effect off.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  const getRecents = useCallback(
    (): TreeSelection[] =>
      filterId && userId ? getRecentEntities(userId, filterId, version) : [],
    [filterId, userId, version, storageRevision]
  );

  const addRecents = useCallback(
    (entities: TreeSelection[]) => {
      if (filterId && userId) {
        addRecentEntities(userId, filterId, entities, version);
      }
    },
    [filterId, userId, version]
  );

  const removeRecents = useCallback(
    (entities: Pick<TreeSelection, 'id' | 'levelId'>[]) => {
      if (filterId && userId) {
        removeRecentEntities(userId, filterId, entities, version);
      }
    },
    [filterId, userId, version]
  );

  return { getRecents, addRecents, removeRecents };
};
